<template>
  <div class="login-page-wrapper">
    <div class="login-container">
      <!-- 左侧装饰区 -->
      <div class="login-left">
        <div class="login-brand">
          <img 
            :src="require('@/assets/images/logo.png')" 
            alt="logo" 
            class="brand-logo"
          />
          <h2 class="brand-title">{{ isEn ? 'Welcome Back' : '欢迎回来' }}</h2>
          <p class="brand-subtitle">{{ isEn ? 'Login to continue using our services' : '登录以继续使用我们的服务' }}</p>
        </div>
        <div class="brand-decoration">
          <div class="decoration-shape"></div>
          <div class="decoration-shape shape-2"></div>
        </div>
      </div>

      <!-- 右侧登录表单 -->
      <div class="login-right">
        <div class="login-card">
          <el-tabs v-model="activeTab" @tab-click="handleTabClick" class="login-tabs">
            <!-- 手机号登录/注册 或 邮箱注册 -->
            <el-tab-pane :label="isEn ? 'Email Register' : '手机号登录/注册'" name="phoneRegister">
              <el-form 
                :model="registerForm" 
                :rules="registerRules" 
                ref="registerForm" 
                label-position="top"
                class="login-form"
              >
                <!-- 英文模式：姓名 + 邮箱 + 密码 -->
                <template v-if="isEn">
                  <el-form-item :label="isEn ? 'Full Name' : '姓名'" prop="name">
                    <el-input 
                      v-model="registerForm.name" 
                      :placeholder="isEn ? 'Please enter your full name' : '请输入姓名'" 
                      prefix-icon="el-icon-user"
                    />
                  </el-form-item>

                  <el-form-item :label="isEn ? 'Email' : '邮箱'" prop="email">
                    <el-input 
                      v-model="registerForm.email" 
                      :placeholder="isEn ? 'Please enter your email' : '请输入邮箱'" 
                      prefix-icon="el-icon-message"
                    />
                  </el-form-item>

                  <el-form-item :label="isEn ? 'Password' : '密码'" prop="password">
                    <el-input 
                      v-model="registerForm.password" 
                      show-password
                      :placeholder="isEn ? 'Please enter password (min 6 characters)' : '请输入密码（最少6位）'"
                      prefix-icon="el-icon-lock"
                    />
                  </el-form-item>
                </template>

                <!-- 中文模式：手机号 + 图形验证码 + 短信验证码 -->
                <template v-else>
                  <el-form-item :label="isEn ? 'Phone' : '手机号'" prop="phone">
                    <el-input v-model="registerForm.phone" :placeholder="isEn ? 'Please enter phone number' : '请输入手机号'" prefix-icon="el-icon-phone"></el-input>
                  </el-form-item>

                  <el-form-item :label="isEn ? 'Captcha' : '图形验证码'" prop="captcha">
                    <el-input v-model="registerForm.captcha" :placeholder="isEn ? 'Please enter captcha' : '请输入图形验证码'">
                      <template #append>
                        <div class="captcha-container">
                          <img 
                            v-if="captchaurl"
                            :src="captchaurl" 
                            alt="图形验证码" 
                            @click="getCaptchaImage"
                            class="captcha-img"
                          />
                          <span v-else class="captcha-loading">加载中...</span>
                        </div>
                      </template>
                    </el-input>
                  </el-form-item>

                  <el-form-item :label="isEn ? 'SMS Code' : '短信验证码'" prop="smsCode">
                    <el-input v-model="registerForm.smsCode" :placeholder="isEn ? 'Please enter SMS code' : '请输入短信验证码'">
                      <template #append>
                        <el-button @click="sendSmsCode" :disabled="smsCodeDisabled" class="sms-btn">
                          {{ smsCodeText }}
                        </el-button>
                      </template>
                    </el-input>
                  </el-form-item>
                </template>
              </el-form>
            </el-tab-pane>

            <!-- 密码登录（邮箱/手机号） -->
            <el-tab-pane :label="isEn ? 'Password Login' : '密码登录'" name="passwordLogin">
              <el-form 
                :model="loginForm" 
                :rules="loginRules" 
                ref="loginForm" 
                label-position="top"
                class="login-form"
              >
                <el-form-item :label="isEn ? 'Email / Phone' : '手机号 / 邮箱'" prop="account">
                  <el-input 
                    v-model="loginForm.account" 
                    :placeholder="isEn ? 'Please enter email or phone' : '请输入手机号或邮箱'" 
                    prefix-icon="el-icon-user"
                  />
                </el-form-item>
                <el-form-item :label="isEn ? 'Password' : '密码'" prop="password">
                  <el-input 
                    v-model="loginForm.password" 
                    show-password
                    :placeholder="isEn ? 'Please enter password' : '请输入密码'"
                    prefix-icon="el-icon-lock"
                  >
                    <template slot="append">
                      <span class="forgot-password" @click="handleForgotPassword">
                        {{ isEn ? 'Forgot password?' : '忘记密码？' }}
                      </span>
                    </template>
                  </el-input>
                </el-form-item>
              </el-form>
            </el-tab-pane>
          </el-tabs>

          <!-- 提交按钮 -->
          <el-button 
            type="primary" 
            class="submit-btn"
            @click="handleSubmit" 
            size="large"
            :loading="isLoading"
          >
            {{ getSubmitButtonText }}
          </el-button>

          <!-- 切换登录方式（英文模式显示） -->
          <div v-if="isEn" class="login-switch">
            <span class="switch-text">
              {{ activeTab === 'phoneRegister' ? 'Already have an account?' : "Don't have an account?" }}
              <span class="switch-link" @click="toggleTab">
                {{ activeTab === 'phoneRegister' ? 'Login' : 'Register' }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { 
  captchaImage,
  postLoginOtp,
  postSigninOtp,
  postLogin,
  authRegister
} from "@/api/auth";

export default {
  name: "LoginPage",
  data() {
    return {
      activeTab: 'phoneRegister',
      profile: {},
      loginForm: {
        account: '',
        password: ''
      },
      loginRules: {
        account: [
          { required: true, message: '请输入账号', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能小于6位', trigger: 'blur' }
        ]
      },
      registerForm: {
        phone: '',
        email: '',
        name: '',
        password: '',
        captcha: '',
        smsCode: '',
        company: '',
        position: ''
      },
      registerRules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/, message: '请输入正确的邮箱格式', trigger: 'blur' }
        ],
        name: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能小于6位', trigger: 'blur' }
        ],
        captcha: [{ required: true, message: '请输入图形验证码', trigger: 'blur' }],
        smsCode: [{ required: true, message: '请输入短信验证码', trigger: 'blur' }]
      },
      captchaurl: '',
      captchaid: '',
      smsCodeDisabled: false,
      smsCodeText: '获取验证码',
      smsCodeCountdown: 60,
      otp: '',
      adLogin: null,
      isLoading: false,
      redirect: null
    };
  },
  computed: {
    isEn() {
      return this.$i18n?.locale === 'en' || localStorage.getItem('locale') === 'en';
    },
    getSubmitButtonText() {
      if (this.isEn) {
        return this.activeTab === 'passwordLogin' ? 'Sign In' : 'Register';
      }
      return this.activeTab === 'passwordLogin' ? '登 录' : '注 册 / 登 录';
    }
  },
  created() {
    this.redirect = this.$route.query.redirect || null;
    this.getCaptchaImage();
  },
  methods: {
    toggleTab() {
      this.activeTab = this.activeTab === 'phoneRegister' ? 'passwordLogin' : 'phoneRegister';
      this.clearValidation();
    },
    clearValidation() {
      if (this.$refs.registerForm) {
        this.$refs.registerForm.clearValidate();
      }
      if (this.$refs.loginForm) {
        this.$refs.loginForm.clearValidate();
      }
    },
    handleTabClick(tab) {
      if (tab.name === 'passwordLogin') {
        if (this.$refs.registerForm) {
          this.$refs.registerForm.clearValidate();
        }
        // 英文模式不需要获取验证码
        if (!this.isEn) {
          this.getCaptchaImage();
        }
      } else if (tab.name === 'phoneRegister') {
        if (this.$refs.loginForm) {
          this.$refs.loginForm.clearValidate();
        }
        if (!this.isEn) {
          this.getCaptchaImage();
        }
      }
    },
    getCaptchaImage() {
      captchaImage().then((result) => {
        if (result.code === 201) {
          const res = result.data;
          this.captchaurl = res.captcha;
          this.captchaid = res.captchaid;
        }
      });
    },
    async sendSmsCode() {
      if (!/^1[3-9]\d{9}$/.test(this.registerForm.phone)) {
        this.$message.error('请输入正确的手机号');
        return;
      }
      try {
        const res = await postSigninOtp(this.registerForm.phone, this.captchaid, this.registerForm.captcha);
        if (res.code === 201) {
          this.$message.success('短信验证码发送成功');
          this.startSmsCountdown();
          this.otp = res.otp;
        } else {
          this.$message.error(res.err || '发送短信验证码失败，请重试');
        }
      } catch (error) {
        this.$message.error('发送短信验证码失败，请重试');
      }
    },
    startSmsCountdown() {
      this.smsCodeDisabled = true;
      this.smsCodeText = `${this.smsCodeCountdown}s 后重试`;
      const timer = setInterval(() => {
        this.smsCodeCountdown--;
        this.smsCodeText = `${this.smsCodeCountdown}s 后重试`;
        if (this.smsCodeCountdown <= 0) {
          clearInterval(timer);
          this.smsCodeDisabled = false;
          this.smsCodeText = '获取验证码';
          this.smsCodeCountdown = 60;
        }
      }, 1000);
    },
    async handleSubmit() {
      if (this.activeTab === 'phoneRegister') {
        await this.submitRegister();
      } else if (this.activeTab === 'passwordLogin') {
        await this.submitPasswordLogin();
      }
    },
    async submitRegister() {
      // 英文模式：邮箱注册
      if (this.isEn) {
        this.$refs.registerForm.validate(async (valid) => {
          if (valid) {
            this.isLoading = true;
            try {
              const response = await authRegister(
                this.registerForm.email,
                this.registerForm.password,
                this.registerForm.name
              );
              if (response.code === 201) {
                this.$message.success('注册成功');
                // 自动切换到登录
                this.activeTab = 'passwordLogin';
                this.loginForm.account = this.registerForm.email;
                this.$refs.registerForm.resetFields();
                this.clearValidation();
              } else {
                this.$message.error(response.err || '注册失败，请重试');
              }
            } catch (error) {
              this.$message.error('注册失败，请重试');
            } finally {
              this.isLoading = false;
            }
          }
        });
        return;
      }

      // 中文模式：手机号登录/注册
      this.$refs.registerForm.validate(async (valid) => {
        if (valid) {
          this.isLoading = true;
          try {
            const response = await postLoginOtp(this.registerForm.phone, { otp: this.registerForm.smsCode });
            if (response.code === 201) {
              this.$message.success('登录成功');
              localStorage.setItem('token', response.data.token);
              localStorage.setItem('initpwd', response.data.initpwd);
              this.handleLoginSuccess();
            } else {
              this.$message.error(response.err || '登录失败，请重试');
            }
          } catch (error) {
            this.$message.error('登录失败，请重试');
          } finally {
            this.isLoading = false;
          }
        }
      });
    },
    async submitPasswordLogin() {
      this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          this.isLoading = true;
          try {
            const response = await postLogin(this.loginForm.account, { password: this.loginForm.password });
            if (response.code === 201) {
              this.$message.success('登录成功');
              localStorage.setItem('token', response.data.token);
              localStorage.setItem('initpwd', response.data.initpwd);
              this.handleLoginSuccess();
            } else {
              this.$message.error(response.err || '登录失败，请重试');
            }
          } catch (error) {
            this.$message.error('登录失败，请重试');
          } finally {
            this.isLoading = false;
          }
        }
      });
    },
    handleLoginSuccess() {
      if (this.redirect) {
        this.$router.push(decodeURIComponent(this.redirect));
      } else {
        this.$router.push({ name: 'Home' });
      }
    },
    handleForgotPassword() {
      this.$router.push({ name: 'ResetPwd' });
    },
    handleLogin(type) {
      console.log(type);
      this.$message.info('敬请期待');
    }
  }
};
</script>

<style scoped>
.login-page-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4f8;
  padding: 20px;
}

.login-container {
  display: flex;
  width: 100%;
  max-width: 1000px;
  min-height: 600px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* ===== 左侧装饰区 ===== */
.login-left {
  flex: 0 0 40%;
  background: linear-gradient(135deg, #1a3a6b 0%, #2a5f8f 100%);
  padding: 48px 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}

.login-brand {
  position: relative;
  z-index: 2;
}

.brand-logo {
  width: 130px;
  height: auto;
  margin-bottom: 32px;
  filter: brightness(0) invert(1);
}

.brand-title {
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.brand-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  margin: 0;
}

.brand-decoration {
  position: relative;
  z-index: 1;
}

.decoration-shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  width: 300px;
  height: 300px;
  bottom: -100px;
  right: -100px;
}

.shape-2 {
  width: 150px;
  height: 150px;
  bottom: 40px;
  right: 40px;
  background: rgba(255, 255, 255, 0.06);
}

/* ===== 右侧表单区 ===== */
.login-right {
  flex: 1;
  padding: 48px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-card {
  width: 100%;
  max-width: 400px;
}

.login-tabs :deep(.el-tabs__nav) {
  display: flex;
  justify-content: center;
  width: 100%;
}

.login-tabs :deep(.el-tabs__item) {
  font-size: 16px;
  font-weight: 500;
  flex: 1;
  text-align: center;
}

.login-tabs :deep(.el-tabs__item.is-active) {
  color: #1a3a6b;
}

.login-tabs :deep(.el-tabs__active-bar) {
  background-color: #1a3a6b;
}

.login-tabs :deep(.el-tabs__content) {
  padding-top: 20px;
}

.login-form :deep(.el-form-item) {
  margin-bottom: 22px;
}

.login-form :deep(.el-form-item__label) {
  font-weight: 500;
  color: #333;
  padding-bottom: 4px;
}

.login-form :deep(.el-input__inner) {
  height: 44px;
  border-radius: 8px;
}

.login-form :deep(.el-input-group__append) {
  padding: 0 12px;
  border-radius: 0 8px 8px 0;
  background: transparent;
  border: none;
}

.captcha-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 90px;
}

.captcha-img {
  height: 32px;
  cursor: pointer;
  border-radius: 4px;
  display: block;
}

.captcha-loading {
  font-size: 12px;
  color: #999;
}

.sms-btn {
  font-size: 13px;
  color: #1a3a6b;
  border: none;
  background: transparent;
  white-space: nowrap;
}

.sms-btn:hover {
  color: #2a5f8f;
}

.forgot-password {
  cursor: pointer;
  font-size: 13px;
  color: #888;
  padding: 0 4px;
}

.forgot-password:hover {
  color: #1a3a6b;
}

.submit-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  margin-top: 8px;
  background: linear-gradient(135deg, #1a3a6b 0%, #2a5f8f 100%);
  border: none;
}

.submit-btn:hover {
  opacity: 0.9;
}

/* ===== 登录切换 ===== */
.login-switch {
  text-align: center;
  margin-top: 16px;
}

.switch-text {
  font-size: 14px;
  color: #888;
}

.switch-link {
  color: #1a3a6b;
  cursor: pointer;
  font-weight: 500;
  margin-left: 4px;
}

.switch-link:hover {
  text-decoration: underline;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    min-height: auto;
    border-radius: 12px;
  }

  .login-left {
    flex: none;
    padding: 32px 24px;
    min-height: 200px;
  }

  .brand-title {
    font-size: 22px;
  }

  .brand-logo {
    width: 100px;
    margin-bottom: 20px;
  }

  .login-right {
    padding: 32px 20px;
  }

  .login-card {
    max-width: 100%;
  }

  .decoration-shape {
    width: 200px;
    height: 200px;
    bottom: -60px;
    right: -60px;
  }
}
</style>